import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../utils/axiosInstance";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function AddRecipient({
  open,
  onClose,
  groupId,
  onRecipientsUpdated,
  filteredUsers = [],
}) {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [stagedUsers, setStagedUsers] = useState([]); // New state for staged users
  const [stagedForRemoval, setStagedForRemoval] = useState([]); // New state for staged removals
  const [availableSearchQuery, setAvailableSearchQuery] = useState("");
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingUsers, setAddingUsers] = useState(new Set());
  const [removingUsers, setRemovingUsers] = useState(new Set());
  const [savingRecipients, setSavingRecipients] = useState(false); // New state for save button

  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Use useMemo for filtering to optimize performance
  const filteredAvailableUsers = React.useMemo(() => {
    if (!availableSearchQuery) return availableUsers;
    return availableUsers.filter(
      (user) =>
        (user.name || "")
          .toLowerCase()
          .includes(availableSearchQuery.toLowerCase()) ||
        (user.email || "")
          .toLowerCase()
          .includes(availableSearchQuery.toLowerCase()) ||
        (user.phone || "")
          .toLowerCase()
          .includes(availableSearchQuery.toLowerCase())
    );
  }, [availableUsers, availableSearchQuery]);

  // Updated to show existing recipients minus staged for removal, plus staged users
  // const allSelectedRecipients = React.useMemo(() => {
  //   const existingRecipients = filteredUsers.filter(member =>
  //     !stagedForRemoval.some(stagedUser => stagedUser.id === member.user.id)
  //   );
  //   return [...stagedUsers, ...existingRecipients];
  // }, [filteredUsers, stagedUsers, stagedForRemoval]);
  // const allSelectedRecipients = React.useMemo(() => {
  //   const existingRecipients = filteredUsers
  //     .filter(member => !stagedForRemoval.some(stagedUser => stagedUser.id === member.user.id))
  //     .map(member => ({
  //       id: member.user.id,
  //       name: member.user.name,
  //       email: member.user.email,
  //       phone: member.user.phone,
  //     }));

  //   return [...stagedUsers, ...existingRecipients];
  // }, [filteredUsers, stagedUsers, stagedForRemoval]);
  const allSelectedRecipients = React.useMemo(() => {
    const existingRecipients = filteredUsers
      .filter(
        (member) =>
          !stagedForRemoval.some((staged) => staged.user.id === member.user.id)
      )
      .map((member) => ({
        id: member.user.id,
        name: member.user.name,
        email: member.user.email,
        phone: member.user.phone,
      }));

    return [...stagedUsers, ...existingRecipients];
  }, [filteredUsers, stagedUsers, stagedForRemoval]);

  const filteredSelectedRecipients = React.useMemo(() => {
    if (!selectedSearchQuery) return allSelectedRecipients;
    return allSelectedRecipients.filter(
      (user) =>
        (user.name || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase()) ||
        (user.email || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase()) ||
        (user.phone || "")
          .toLowerCase()
          .includes(selectedSearchQuery.toLowerCase())
    );
  }, [allSelectedRecipients, selectedSearchQuery]);

  useEffect(() => {
    if (open) {
      fetchAvailableUsers();
      setStagedUsers([]); // Clear staged users when dialog opens
      setStagedForRemoval([]); // Clear staged removals when dialog opens
    }
    // Reset search queries when dialog opens
    setAvailableSearchQuery("");
    setSelectedSearchQuery("");
  }, [open, filteredUsers]);

  const fetchAvailableUsers = async () => {
    try {
      setLoading(true);

      // Fetch all available users
      // const allUsersResponse = await axios.get(
      //   // `${process.env.REACT_APP_API_GATEAWAY}/users/unlinked/${groupId}?companyId=${process.env.REACT_APP_COMPANY_ID}`

      // );
      const queryParams = new URLSearchParams({
        companyId: process.env.REACT_APP_COMPANY_ID,

        type: "members",
      });

      if (groupId) {
        queryParams.append("groupId", groupId);
      }

      const allUsersResponse = await axios.get(
        `${
          process.env.REACT_APP_API_GATEAWAY
        }/users/unlinked?${queryParams.toString()}`
      );
      const allUsers = allUsersResponse.data || [];

      // Filter out users who are already in the group and not staged for removal
      const currentRecipientIds = new Set(
        filteredUsers
          .filter(
            (member) =>
              !stagedForRemoval.some(
                (stagedUser) => stagedUser.id === member.user.id
              )
          )
          .map((member) => member.user.id)
      );
      const stagedUserIds = new Set(stagedUsers.map((user) => user.id));

      const available = allUsers.filter(
        (user) =>
          !currentRecipientIds.has(user.id) && !stagedUserIds.has(user.id)
      );

      // Add users staged for removal to available users
      const availableWithStaged = [...available, ...stagedForRemoval];

      setAvailableUsers(availableWithStaged);
    } catch (error) {
      console.error("Error fetching users:", error);
      showNotification("Error fetching users", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Updated to only move user to staged area (no API call)
  const MoveAddRecipient = async (user) => {
    setAddingUsers((prev) => new Set([user.id, ...prev]));
    try {
      // Move user from available to staged
      setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
      setStagedUsers((prev) => [user, ...prev]);

      setAddingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    } catch (error) {
      console.error(`Error staging recipient ${user.id}:`, error);
      showNotification(`Error staging ${user.name}`, "error");
    }
  };

  // Updated to handle actual API calls for both adding and removing
  const handleSaveChanges = async () => {
    if (stagedUsers.length === 0 && stagedForRemoval.length === 0) {
      showNotification("No changes to save", "warning");
      return;
    }

    setSavingRecipients(true);
    try {
      // Handle additions
      if (stagedUsers.length > 0) {
        const userIds = stagedUsers.map((user) => user.id);

        // await axios.post(
        //   `${process.env.REACT_APP_API_GATEAWAY}/recipients/user`,
        //   {
        //     notificationGroupId: groupId,
        //     userIds: userIds,
        //   }
        // );

        await axios.post(`${process.env.REACT_APP_API_GATEAWAY}/members`, {
          UI_Description: `${getUIDescription()}\n Add one or many members`,
          userIds: userIds,
          groupId,
          emailEnable: true,
          smsEnable: true,
        });
      }

      // Handle removals
      // if (stagedForRemoval.length > 0) {
      //   const memberIds = stagedForRemoval.map(member => member.id);
      //   const body = { ids: memberIds };

      //   await axios.delete(
      //     // `${process.env.REACT_APP_API_GATEAWAY}/recipients/notificationGroup/${groupId}`,
      //     `${process.env.REACT_APP_API_GATEAWAY}/members`,

      //     {
      //       data: body,
      //     }
      //   );
      // }
      if (stagedForRemoval.length > 0) {
        const memberIds = stagedForRemoval.map((member) => member.id);
        const body = { UI_Description: `${getUIDescription()}\n Delete one or many members`, ids: memberIds };

        await axios.delete(`${process.env.REACT_APP_API_GATEAWAY}/members`, {
          data: body,
        });
      }

      const addedCount = stagedUsers.length;
      const removedCount = stagedForRemoval.length;
      let message = "";

      if (addedCount > 0 && removedCount > 0) {
        message = `${addedCount} user(s) added and ${removedCount} user(s) removed successfully`;
      } else if (addedCount > 0) {
        message = `${addedCount} user(s) added successfully`;
      } else if (removedCount > 0) {
        message = `${removedCount} user(s) removed successfully`;
      }

      showNotification(message, "success");

      // Clear staged users after successful save
      setStagedUsers([]);
      setStagedForRemoval([]);

      // Refresh parent component data
      if (onRecipientsUpdated) {
        onRecipientsUpdated();
      }
    } catch (error) {
      console.error(`Error saving changes:`, error);
      showNotification(`Error saving changes`, "error");

      // Revert changes on error
      setAvailableUsers((prev) =>
        prev.filter(
          (user) =>
            !stagedUsers.some((stagedUser) => stagedUser.id === user.id) &&
            !stagedForRemoval.some((stagedUser) => stagedUser.id === user.id)
        )
      );
      setStagedUsers([]);
      setStagedForRemoval([]);
    } finally {
      setSavingRecipients(false);
    }
  };

  // Updated to stage removals instead of immediate deletion
  // const handleRemoveRecipient = async (user) => {
  //   // Check if user is in staged users (not yet saved)
  //   const isStagedUser = stagedUsers.some(stagedUser => stagedUser.id === user.id);

  //   if (isStagedUser) {
  //     // Just move back to available users (no API call needed)
  //     setStagedUsers(prev => prev.filter(u => u.id !== user.id));
  //     setAvailableUsers(prev => [user, ...prev]);
  //     showNotification(`${user.name} removed from selection`, "success");
  //     return;
  //   }

  //   // Stage existing recipient for removal (no immediate API call)
  //   setStagedForRemoval(prev => [user, ...prev]);
  //   setAvailableUsers(prev => [user, ...prev]);
  //   showNotification(`${user.name} staged for removal`, "success");
  // };
  const handleRemoveRecipient = async (user) => {
    const isStagedUser = stagedUsers.some(
      (stagedUser) => stagedUser.id === user.id
    );

    if (isStagedUser) {
      setStagedUsers((prev) => prev.filter((u) => u.id !== user.id));
      setAvailableUsers((prev) => [user, ...prev]);
      showNotification(`${user.name} removed from selection`, "success");
      return;
    }

    // Find the member from filteredUsers
    const memberToRemove = filteredUsers.find(
      (member) => member.user.id === user.id
    );
    if (!memberToRemove) return;

    setStagedForRemoval((prev) => [memberToRemove, ...prev]);
    setAvailableUsers((prev) => [memberToRemove.user, ...prev]); // Push only the user to available
    showNotification(
      `${memberToRemove.user.name} staged for removal`,
      "success"
    );
  };

  // Handle adding user back from staged removal
  // const handleAddFromStaged = (user) => {
  //   // Check if user is staged for removal
  //   const isStagedForRemoval = stagedForRemoval.some(stagedUser => stagedUser.id === user.id);

  //   if (isStagedForRemoval) {
  //     // Remove from staged removal (user goes back to selected recipients)
  //     setStagedForRemoval(prev => prev.filter(u => u.id !== user.id));
  //     setAvailableUsers(prev => prev.filter(u => u.id !== user.id));
  //     showNotification(`${user.name} restored to recipients`, "success");
  //     return;
  //   }

  //   // Normal add to staged users
  //   MoveAddRecipient(user);
  // };
  const handleAddFromStaged = (user) => {
    const isStagedForRemoval = stagedForRemoval.some(
      (member) => member.user.id === user.id
    );

    if (isStagedForRemoval) {
      setStagedForRemoval((prev) =>
        prev.filter((member) => member.user.id !== user.id)
      );
      setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
      showNotification(`${user.name} restored to recipients`, "success");
      return;
    }

    MoveAddRecipient(user);
  };

  // Updated to handle unsaved changes when closing
  const handleClose = () => {
    if (stagedUsers.length > 0 || stagedForRemoval.length > 0) {
      // Move staged users back to available
      setAvailableUsers((prev) => {
        const filtered = prev.filter(
          (user) =>
            !stagedUsers.some((stagedUser) => stagedUser.id === user.id) &&
            !stagedForRemoval.some((stagedUser) => stagedUser.id === user.id)
        );
        return filtered;
      });
      setStagedUsers([]);
      setStagedForRemoval([]);
    }
    onClose();
  };

  const handleSaveAndClose = async () => {
    await handleSaveChanges();
    if (onRecipientsUpdated) {
      onRecipientsUpdated();
    }
    onClose();
  };

  // Updated UserListItem to show different styling for staged users and staged removals
  const UserListItem = ({ user, showAddButton, showRemoveButton }) => {
    const isStagedUser = stagedUsers.some(
      (stagedUser) => stagedUser.id === user.id
    );
    const isStagedForRemoval = stagedForRemoval.some(
      (stagedUser) => stagedUser.id === user.id
    );
    // const isExistingRecipient = filteredUsers.some(device => device.id === user.id) && !isStagedForRemoval;
    const isExistingRecipient =
      filteredUsers.some((member) => member.user.id === user.id) &&
      !isStagedForRemoval;

    return (
      <ListItem
        sx={{
          border: 1,
          borderColor: isStagedUser
            ? "success.main"
            : isStagedForRemoval
            ? "error.main"
            : "divider",
          borderRadius: 1,
          mb: 1,
          backgroundColor: isStagedUser
            ? "success.light"
            : isStagedForRemoval
            ? "error.light"
            : "background.paper",
          opacity: isStagedForRemoval ? 0.6 : isStagedUser ? 0.9 : 1,
          "&:hover": {
            backgroundColor: isStagedUser
              ? "success.light"
              : isStagedForRemoval
              ? "error.light"
              : "action.hover",
          },
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{
                  textDecoration: isStagedForRemoval ? "line-through" : "none",
                  color: isStagedForRemoval ? "error.dark" : "inherit",
                }}
              >
                {user.name}
              </Typography>
              {isStagedUser && (
                <Typography
                  variant="caption"
                  color="success.dark"
                  fontWeight="bold"
                >
                  (TO ADD)
                </Typography>
              )}
              {isStagedForRemoval && (
                <Typography
                  variant="caption"
                  color="error.dark"
                  fontWeight="bold"
                >
                  (TO REMOVE)
                </Typography>
              )}
              {isExistingRecipient && !isStagedUser && !isStagedForRemoval && (
                <Typography
                  variant="caption"
                  color="primary.dark"
                  fontWeight="bold"
                >
                  (CURRENT)
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: isStagedForRemoval ? "line-through" : "none",
                }}
              >
                {user.email}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: isStagedForRemoval ? "line-through" : "none",
                }}
              >
                {user.phone}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          {showAddButton && (
            <IconButton
              edge="end"
              onClick={() => handleAddFromStaged(user)}
              disabled={addingUsers.has(user.id)}
              sx={{
                backgroundColor: isStagedForRemoval
                  ? "primary.main"
                  : "success.main",
                color: "white",
                "&:hover": {
                  backgroundColor: isStagedForRemoval
                    ? "primary.dark"
                    : "success.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {addingUsers.has(user.id) ? (
                <CircularProgress size={20} color="#60a5fa" />
              ) : (
                <AddIcon />
              )}
            </IconButton>
          )}
          {showRemoveButton && (
            <IconButton
              edge="end"
              onClick={() => handleRemoveRecipient(user)}
              disabled={removingUsers.has(user.id)}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {removingUsers.has(user.id) ? (
                <CircularProgress size={20} color="#60a5fa" />
              ) : (
                <DeleteIcon />
              )}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "800px",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Add recipient{" "}
              {(stagedUsers.length > 0 || stagedForRemoval.length > 0) &&
                `(${stagedUsers.length} to add, ${stagedForRemoval.length} to remove)`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <CircularProgress color="#60a5fa" size={60} />
            </Box>
          ) : (
            <>
              {/* Warning Message */}
              <Typography
                variant="body2"
                color="error"
                sx={{ mb: 2, fontStyle: "italic" }}
              >
                Selected Recipients will receive notifications from following
                units
              </Typography>

              {/* Staged changes info */}
              {(stagedUsers.length > 0 || stagedForRemoval.length > 0) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {stagedUsers.length > 0 &&
                    `${stagedUsers.length} user(s) to be added`}
                  {stagedUsers.length > 0 &&
                    stagedForRemoval.length > 0 &&
                    ", "}
                  {stagedForRemoval.length > 0 &&
                    `${stagedForRemoval.length} user(s) to be removed`}
                  . Click "Save Changes" to apply.
                </Alert>
              )}

              <Box sx={{ display: "flex", height: "500px", gap: 2 }}>
                {/* Left Panel - Available Users */}
                <Box sx={{ width: "50%" }}>
                  <Paper
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "grey.800",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.700",
                        color: "white",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        AVAILABLE USERS
                      </Typography>
                    </Box>

                    {/* Search */}
                    <Box sx={{ p: 2 }}>
                      <TextField
                        placeholder="SEARCH"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={availableSearchQuery}
                        onChange={(e) =>
                          setAvailableSearchQuery(e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "black",
                          },
                        }}
                      />
                    </Box>

                    {/* Users List */}
                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
                      <List sx={{ py: 0 }}>
                        {filteredAvailableUsers.map((user) => (
                          <UserListItem
                            key={user.id}
                            user={user}
                            showAddButton={true}
                            showRemoveButton={false}
                          />
                        ))}
                        {filteredAvailableUsers.length === 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 4, color: "grey.400" }}
                          >
                            No available users found
                          </Typography>
                        )}
                      </List>
                    </Box>
                  </Paper>
                </Box>

                {/* Right Panel - Selected Recipients */}
                <Box sx={{ width: "50%" }}>
                  <Paper
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "grey.800",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.700",
                        color: "white",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        SELECTED RECIPIENTS
                      </Typography>
                    </Box>

                    {/* Search */}
                    <Box sx={{ p: 2 }}>
                      <TextField
                        placeholder="SEARCH"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={selectedSearchQuery}
                        onChange={(e) => setSelectedSearchQuery(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "black",
                          },
                        }}
                      />
                    </Box>

                    {/* Recipients List */}
                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
                      <List sx={{ py: 0 }}>
                        {filteredSelectedRecipients.map((user) => (
                          <UserListItem
                            key={user.id}
                            user={user}
                            showAddButton={false}
                            showRemoveButton={true}
                          />
                        ))}
                        {filteredSelectedRecipients.length === 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 4, color: "grey.400" }}
                          >
                            No recipients selected
                          </Typography>
                        )}
                      </List>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: 0,
              bgcolor: "grey.400",
              color: "white",
              "&:hover": {
                bgcolor: "grey.500",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAndClose}
            disabled={
              (stagedUsers.length === 0 && stagedForRemoval.length === 0) ||
              savingRecipients
            }
            sx={{
              bgcolor: "grey.600",
              "&:hover": {
                bgcolor: "grey.700",
              },
              "&:disabled": {
                bgcolor: "grey.300",
              },
            }}
          >
            {savingRecipients ? (
              <>
                <CircularProgress size={20} color="#60a5fa" sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              `Save Changes ${
                stagedUsers.length > 0 || stagedForRemoval.length > 0
                  ? `(+${stagedUsers.length}, -${stagedForRemoval.length})`
                  : ""
              }`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
